import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';

import {
  AttendanceCalendarComponent,
  AttendanceData,
} from '../components/attendance-calendar/attendance-calendar.component';
import { SuccessToastComponent } from '../shared/components/success-toast/success-toast.component';
import { RoleService } from '../services/role.service';
import { UserRole } from '../shared/types/role.interface';
import {
  AttendanceService,
  AttendanceRecord,
  WorkHours,
  BreakHours,
} from '../services/attendance.service';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';
import { NotificationService } from '../services/notification.service'; // NotificationService;
import { getUserTimezone } from '../shared/utils/date.util';

// Constants
const TOAST_AUTO_HIDE_DELAY = 3000;
const DEFAULT_HOURS = '0h 0m';
const DEFAULT_BREAK_HOURS = '0m';
const DEFAULT_CALENDAR_HOURS = '00:00';

// Time periods for greeting
const AFTERNOON_START = 12;
const EVENING_START = 17;
const NIGHT_START = 20;
const NIGHT_END = 5;

// Image paths
const ATTENDANCE_IMAGES = {
  DAY: '/assets/images/attendance/check-in.png',
  NIGHT: '/assets/images/attendance/night_cloud.png',
} as const;

// Interfaces
interface AttendanceTimeRecord {
  type: 'In' | 'Out';
  time: string;
  sessionIndex?: number;
}

interface DailySummary {
  totalHours: string;
  breakHours: string;
  records: AttendanceTimeRecord[];
}

type ToastType = 'checkin' | 'checkout';
type AttendanceType = 'In' | 'Out';

@Component({
  selector: 'app-attendance',
  standalone: true,
  imports: [CommonModule, AttendanceCalendarComponent, SuccessToastComponent],
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 0, transform: 'translateY(-10px)' })
        ),
      ]),
    ]),
  ],
})
export class AttendanceComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private toastTimeoutId?: number;

  // User and role data
  userRole: UserRole | null = null;
  userName = 'User';
  greeting = 'Good morning';

  // Attendance state
  currentAttendance: AttendanceRecord | null = null;
  isCheckedIn = false;
  isLoading = false;
  isInitialLoadComplete = false;

  // Toast state
  showSuccessToast = false;
  toastType: ToastType = 'checkin';

  // Time tracking - display only API data
  currentWorkHours = DEFAULT_HOURS;
  currentBreakHours = DEFAULT_BREAK_HOURS;

  // Calendar data
  attendanceData: AttendanceData[] = [];
  attendanceRecords: AttendanceRecord[] = [];
  selectedDate = new Date();
  currentMonth = new Date();
  attendanceToday = new Date();

  // Daily summary
  dailySummary: DailySummary = {
    totalHours: DEFAULT_HOURS,
    breakHours: DEFAULT_BREAK_HOURS,
    records: [],
  };

  constructor(
    private readonly roleService: RoleService,
    private readonly attendanceService: AttendanceService,
    private readonly authService: AuthService,
    private readonly logger: LoggerService,
    private readonly messageService: NotificationService,
    // Angular 22 runs this app zoneless: state changed inside an HTTP/timer
    // callback updates the field but never repaints on its own. Without an
    // explicit markForCheck the check-in button stayed on "Loading..." forever
    // even though the status request had already come back.
    private readonly cdr: ChangeDetectorRef
  ) {}

  /**
   * Initialize component and load initial data
   */
  ngOnInit(): void {
    this.attendanceToday = this.getAttendanceToday();
    this.selectedDate = this.attendanceToday;
    this.currentMonth = this.attendanceToday;
    this.userRole = this.roleService.getCurrentUserRole();
    this.setUserInfo();
    this.setGreeting();
    this.initializeAttendance();
    this.loadCalendarData();
  }

  /**
   * Clean up subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.toastTimeoutId !== undefined) {
      clearTimeout(this.toastTimeoutId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Public getters for template
  get currentDateString(): string {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      ...this.getAttendanceTimeZoneOption(),
    });
  }

  get currentTimeString(): string {
    return new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...this.getAttendanceTimeZoneOption(),
    });
  }

  private getAttendanceDisplayTimezone(): string {
    return getUserTimezone();
  }

  private getAttendanceTimeZoneOption(): Pick<Intl.DateTimeFormatOptions, 'timeZone'> {
    const timeZone = this.getAttendanceDisplayTimezone();
    if (!timeZone) {
      return {};
    }
    try {
      new Intl.DateTimeFormat('en-US', { timeZone }).format();
      return { timeZone };
    } catch {
      return {};
    }
  }

  private getAttendanceDisplayHour(): number {
    return Number(
      new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        hourCycle: 'h23',
        ...this.getAttendanceTimeZoneOption(),
      })
    );
  }

  private getAttendanceToday(): Date {
    const timeZone = this.getAttendanceTimeZoneOption().timeZone;
    if (!timeZone) {
      return new Date();
    }
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(new Date())
      .reduce<Record<string, string>>((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});
    return new Date(Number(parts['year']), Number(parts['month']) - 1, Number(parts['day']));
  }

  get selectedDateString(): string {
    return this.selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get buttonText(): string {
    return this.isCheckedIn ? 'Check Out' : 'Check In';
  }

  get toastTitle(): string {
    return this.toastType === 'checkin' ? 'Checked In!' : 'Checked Out!';
  }

  get toastMessage(): string {
    return this.toastType === 'checkin'
      ? "You're now clocked in and ready."
      : "You're clocked out. See you next time.";
  }

  get greetingText(): string {
    return `${this.greeting}, ${this.userName}!`;
  }

  get attendanceImage(): string {
    const hour = this.getAttendanceDisplayHour();
    return hour >= NIGHT_START || hour < NIGHT_END
      ? ATTENDANCE_IMAGES.NIGHT
      : ATTENDANCE_IMAGES.DAY;
  }

  get dailyRecords(): AttendanceTimeRecord[] {
    return this.dailySummary.records;
  }

  get totalHours(): string {
    return this.dailySummary.totalHours;
  }

  get breakHoursDisplay(): string {
    return this.dailySummary.breakHours;
  }

  /**
   * Calculate a displayable hours string for an out attendance record using the session data for the currently selected date.
   * @example
   * acalculateHours(outRecord, inRecord)
   * '01:00'
   * @param {{AttendanceTimeRecord}} {{outRecord}} - The attendance record representing the checkout event (used to locate the session index and determine active state).
   * @param {{AttendanceTimeRecord}} {{inRecord}} - The attendance record representing the checkin event (not used by this implementation but accepted for signature compatibility).
   * @returns {{string}} Return a formatted work hours string, 'Active' for an ongoing session, or the DEFAULT_HOURS fallback when data is unavailable.
   **/
  calculateHours(outRecord: AttendanceTimeRecord, inRecord: AttendanceTimeRecord): string {
    if (outRecord.sessionIndex === undefined) {
      return DEFAULT_HOURS;
    }

    const attendanceRecord = this.findAttendanceRecordForDate(this.selectedDate);
    if (!attendanceRecord?.sessions || attendanceRecord.sessions.length <= outRecord.sessionIndex) {
      return DEFAULT_HOURS;
    }

    const session = attendanceRecord.sessions[outRecord.sessionIndex];
    const workHours = this.getSessionField(session, 'work_hours', 'session_work_hours');
    const checkOut = this.getSessionField(session, 'check_out', 'session_check_out');

    if (!workHours) {
      return DEFAULT_HOURS;
    }

    // Handle active sessions
    if (!checkOut && outRecord.time === 'Active') {
      return 'Active';
    }

    return this.formatSessionWorkHours(workHours);
  }

  // Event handlers
  /**
   * Handle date selection from calendar
   */
  onDateSelected(date: Date): void {
    this.selectedDate = date;
    this.updateDailySummary(date);
  }

  /**
   * Handle month change from calendar
   */
  onMonthChanged(date: Date): void {
    this.currentMonth = date;
    this.loadCalendarData();
  }

  /**
   * Handle toast close event
   */
  onToastClose(): void {
    this.showSuccessToast = false;
  }

  /**
   * Handle clock in action
   */
  clockIn(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    this.attendanceService
      .clockIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoading = false;
          if (response.status) {
            this.handleSuccessfulClockIn();
          } else {
            this.showErrorMessage('Failed to check in', response.message);
          }
          this.cdr.markForCheck();
        },
        error: error => {
          this.isLoading = false;
          this.showErrorMessage('Failed to check in. Please try again.', error.error?.message);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Handle clock out action
   */
  clockOut(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    this.attendanceService
      .clockOut()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoading = false;
          if (response.status) {
            this.handleSuccessfulClockOut();
          } else if (this.isAutoCheckoutError(response.message)) {
            this.handleAutoCheckout();
          } else {
            this.showErrorMessage('Failed to check out', response.message);
          }
          this.cdr.markForCheck();
        },
        error: error => {
          this.isLoading = false;
          const errorMessage = error.error?.message || '';

          // Check if user was auto-checked out after 11:00 PM
          if (this.isAutoCheckoutError(errorMessage)) {
            this.handleAutoCheckout();
          } else {
            this.showErrorMessage('Failed to check out. Please try again.', errorMessage);
          }
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Check if the error is due to automatic checkout after 11:00 PM
   */
  private isAutoCheckoutError(message: string | undefined): boolean {
    if (!message) return false;
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('not checked in') ||
      lowerMessage.includes('has not checked in') ||
      lowerMessage.includes('employee has not checked in')
    );
  }

  /**
   * Handle the case when user was automatically checked out by the system
   */
  private handleAutoCheckout(): void {
    // Update UI to reflect checked-out state
    this.isCheckedIn = false;
    this.attendanceService.clearAttendanceData();

    // Refresh attendance data from server
    this.loadCurrentAttendanceStatus();
    this.loadCalendarData();

    // Show informational message instead of error
    this.messageService.add({
      severity: 'info',
      summary: 'Auto Check-Out',
      detail: 'You were automatically checked out at 11:00 PM. Your attendance has been updated.',
    });
  }

  // Private methods
  /**
   * Initializes attendance subscriptions, updates component state when attendance changes, and loads the current attendance status.
   * @example
   * initializeAttendance()
   * void
   * @returns {void} No value is returned; the method sets up subscriptions and updates component state.
   **/
  private initializeAttendance(): void {
    this.attendanceService.currentAttendance$
      .pipe(takeUntil(this.destroy$))
      .subscribe(attendance => {
        this.currentAttendance = attendance;
        if (attendance) {
          this.updateHoursFromAttendance(attendance);
          this.isCheckedIn = attendance.work_status === 'checked_in';
        }
        this.cdr.markForCheck();
      });

    this.attendanceService.isCheckedIn$.pipe(takeUntil(this.destroy$)).subscribe(isCheckedIn => {
      this.isCheckedIn = isCheckedIn;
      this.cdr.markForCheck();
    });

    this.loadCurrentAttendanceStatus();
  }

  /**
   * Loads the current attendance status from the attendance service and updates component state.
   * @example
   * this.loadCurrentAttendanceStatus()
   * undefined
   * @returns {{void}} Performs side effects: sets isInitialLoadComplete on success, logs errors and falls back to loadTodaysAttendance() on failure.
   **/
  private loadCurrentAttendanceStatus(): void {
    this.attendanceService
      .getCurrentAttendanceStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isInitialLoadComplete = true;
          this.cdr.markForCheck();
        },
        error: error => {
          this.logger.error(
            'Error loading current attendance status',
            error,
            'AttendanceComponent'
          );
          this.loadTodaysAttendance();
        },
      });
  }

  /**
   * Loads today's attendance from the attendance service, updates the stored attendance status on success, and sets the initial-load flag; errors are logged.
   * @example
   * this.loadTodaysAttendance()
   * undefined
   * @returns {void} Performs side effects (updates attendance state and logs errors) and does not return a value.
   */
  private loadTodaysAttendance(): void {
    this.attendanceService
      .getTodaysAttendance()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            this.attendanceService.updateAttendanceStatus(response.data);
          }
          this.isInitialLoadComplete = true;
          this.cdr.markForCheck();
        },
        error: error => {
          this.logger.error("Error loading today's attendance", error, 'AttendanceComponent');
          this.isInitialLoadComplete = true;
          this.cdr.markForCheck();
        },
      });
  }

  private formatDateForAPI(date: Date): string {
    // Format date in local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Load attendance data for the current month, populate calendar attendanceData and attendanceRecords, and update the daily summary.
   * @example
   * loadCalendarData()
   * void
   * @returns {{void}} No return value; updates component state and UI.
   **/
  private loadCalendarData(): void {
    const startDate = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const endDate = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);

    const startDateStr = this.formatDateForAPI(startDate);
    const endDateStr = this.formatDateForAPI(endDate);

    this.attendanceService
      .getAttendanceForPeriod(startDateStr, endDateStr)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            this.attendanceRecords = response.data;
            this.attendanceData = this.transformAttendanceDataForCalendar(response.data);
          } else {
            this.attendanceData = [];
            this.attendanceRecords = [];
          }
          this.updateDailySummary(this.selectedDate);
          this.cdr.markForCheck();
        },
        error: error => {
          this.logger.error('Error loading calendar data', error, 'AttendanceComponent');
          this.attendanceData = [];
          this.updateDailySummary(this.selectedDate);
          this.cdr.markForCheck();
        },
      });
  }

  private handleSuccessfulClockIn(): void {
    this.isCheckedIn = true;
    this.toastType = 'checkin';
    this.showSuccessToast = true;
    this.updateCurrentTime();
    this.loadCurrentAttendanceStatus();
    this.loadCalendarData(); // Refresh calendar data to update time log immediately
    this.autoHideToast();
  }

  private handleSuccessfulClockOut(): void {
    this.isCheckedIn = false;
    this.toastType = 'checkout';
    this.showSuccessToast = true;
    this.updateCurrentTime();
    this.loadCurrentAttendanceStatus();
    this.loadCalendarData(); // Refresh calendar data to update time log immediately
    this.autoHideToast();
  }

  private autoHideToast(): void {
    if (this.toastTimeoutId !== undefined) {
      clearTimeout(this.toastTimeoutId);
    }
    this.toastTimeoutId = window.setTimeout(() => {
      this.showSuccessToast = false;
      this.toastTimeoutId = undefined;
      this.cdr.markForCheck();
    }, TOAST_AUTO_HIDE_DELAY);
  }

  private showErrorMessage(title: string, message?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message || title,
    });
  }

  private updateCurrentTime(): void {
    // No longer calculating work hours - display only API data
  }

  private updateHoursFromAttendance(attendance: AttendanceRecord): void {
    // Always display API data for work hours
    this.currentWorkHours = this.formatWorkHours(attendance.work_hours);

    // Always display API data for break hours
    this.currentBreakHours = this.formatBreakHours(attendance.break_hours);
  }

  /**
   * Update the component's daily summary for the specified date, using an attendance record if available or default values otherwise.
   * @example
   * updateDailySummary(new Date('2026-03-17'))
   * undefined
   * @param {Date} date - The date for which to compute and set the daily summary.
   * @returns {void} No return value; updates the component's this.dailySummary property.
   **/
  private updateDailySummary(date: Date): void {
    const attendanceRecord = this.findAttendanceRecordForDate(date);

    if (attendanceRecord?.sessions) {
      this.populateDailySummaryFromSessions(attendanceRecord);
    } else {
      this.dailySummary = {
        totalHours: DEFAULT_HOURS,
        breakHours: DEFAULT_BREAK_HOURS,
        records: [],
      };
    }
  }

  private findAttendanceRecordForDate(date: Date): AttendanceRecord | undefined {
    const selectedDateString = this.formatDateForComparison(date);
    return this.attendanceRecords.find(record => {
      // Parse date string without timezone conversion (extract YYYY-MM-DD from ISO string)
      const recordDateString = this.parseDateStringWithoutTimezone(record.date);
      return recordDateString === selectedDateString;
    });
  }

  /**
   * Populate the component's dailySummary from an AttendanceRecord's sessions and formatted hours.
   * @example
   * populateDailySummaryFromSessions(attendanceRecord)
   * // this.dailySummary =>
   * // {
   * //   totalHours: '8h',
   * //   breakHours: '1h',
   * //   records: [
   * //     { type: 'In', time: '09:00', sessionIndex: 0 },
   * //     { type: 'Out', time: '17:00', sessionIndex: 0 },
   * //     { type: 'In', time: '18:00', sessionIndex: 1 },
   * //     { type: 'Out', time: 'Active', sessionIndex: 1 }
   * //   ]
   * // }
   * @param {AttendanceRecord} attendanceRecord - Attendance record containing sessions, work_hours, and break_hours.
   * @returns {void} Updates the component's dailySummary property and does not return a value.
   **/
  private populateDailySummaryFromSessions(attendanceRecord: AttendanceRecord): void {
    const sessions = attendanceRecord.sessions || [];
    const records: AttendanceTimeRecord[] = [];

    sessions.forEach((session, sessionIndex) => {
      const checkIn = this.getSessionField(session, 'check_in', 'session_check_in');
      const checkOut = this.getSessionField(session, 'check_out', 'session_check_out');

      if (checkIn) {
        records.push({
          type: 'In',
          time: this.formatTimeForDisplay(checkIn),
          sessionIndex: sessionIndex,
        });
      }

      if (checkOut) {
        records.push({
          type: 'Out',
          time: this.formatTimeForDisplay(checkOut),
          sessionIndex: sessionIndex,
        });
      } else if (checkIn && !checkOut) {
        records.push({
          type: 'Out',
          time: 'Active',
          sessionIndex: sessionIndex,
        });
      }
    });

    this.dailySummary = {
      totalHours: this.formatWorkHours(attendanceRecord.work_hours),
      breakHours: this.formatBreakHours(attendanceRecord.break_hours),
      records: records,
    };
  }

  private transformAttendanceDataForCalendar(
    attendanceRecords: AttendanceRecord[]
  ): AttendanceData[] {
    return attendanceRecords.map(record => ({
      date: record.date.split('T')[0],
      hours: this.formatWorkHoursForCalendar(record.work_hours),
      isPresent: record.status === 'present',
    }));
  }

  private setUserInfo(): void {
    const user = this.authService.user();
    if (user?.name) {
      // Extract only the first name
      const [firstName] = user.name.split(' ');
      this.userName = firstName;
    }
  }

  private setGreeting(): void {
    const currentHour = this.getAttendanceDisplayHour();

    if (currentHour < AFTERNOON_START) {
      this.greeting = 'Good morning';
    } else if (currentHour >= AFTERNOON_START && currentHour < EVENING_START) {
      this.greeting = 'Good afternoon';
    } else {
      this.greeting = 'Good evening';
    }
  }

  // Utility methods
  private formatDateForComparison(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Parse date string without timezone conversion
   * Extracts YYYY-MM-DD from ISO string and returns it as-is
   * This prevents UTC dates like "2026-01-27T00:00:00.000Z" from being converted to previous day
   */
  private parseDateStringWithoutTimezone(dateString: string): string {
    // Extract date part (YYYY-MM-DD) from ISO string without timezone conversion
    return dateString.split('T')[0];
  }

  /**
   * Formats an ISO date-time string into a 12-hour time string (hh:mm:ss am/pm) with a space before and lowercase am/pm.
   * @example
   * formatTimeForDisplay('2021-08-01T14:05:06Z')
   * "02:05:06 pm"
   * @param {{string}} {{dateTimeString}} - The input date-time string to format (e.g., ISO 8601).
   * @returns {{string}} The formatted time string in 12-hour format with lowercase am/pm.
   **/
  private formatTimeForDisplay(dateTimeString: string): string {
    const timeString = new Date(dateTimeString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    // Add space before am/pm and make them lowercase
    return timeString.replace(/(\d{2}:\d{2}:\d{2})\s*(AM|PM)/g, (_match, time, period) => {
      return `${time} ${period.toLowerCase()}`;
    });
  }

  /**
   * Format a WorkHours value (object, 'HH:MM' string, or null) into a human-readable "Xh Ym" string.
   * @example
   * formatWorkHours({ hours: 2, minutes: 30 })
   * "2h 30m"
   * @param {WorkHours|string|null} workHours - Work hours as an object with hours/minutes, a "HH:MM" string, or null.
   * @returns {string} Formatted hours string like "Xh Ym" or a default hours string when input is falsy.
   **/
  private formatWorkHours(workHours: WorkHours | string | null): string {
    if (!workHours) return DEFAULT_HOURS;

    if (typeof workHours === 'string' && workHours.includes(':')) {
      const [hours, minutes] = workHours.split(':');
      return `${hours}h ${minutes}m`;
    }

    if (typeof workHours === 'object') {
      const hours = workHours.hours || 0;
      const minutes = workHours.minutes || 0;
      return `${hours}h ${minutes}m`;
    }

    return workHours.toString();
  }

  /**
   * Format a BreakHours object into a minute string (e.g., "30m"); returns DEFAULT_BREAK_HOURS if input is empty or invalid.
   * @example
   * formatBreakHours({ seconds: 90 })
   * "1m"
   * @param {BreakHours | Record<string, any>} breakHours - Break hours object which may contain minutes and/or seconds.
   * @returns {string} Returns a formatted minutes string (e.g., "30m") or a default label when input is empty/invalid.
   **/
  private formatBreakHours(breakHours: BreakHours | Record<string, any>): string {
    if (!breakHours || Object.keys(breakHours).length === 0) return DEFAULT_BREAK_HOURS;

    if (typeof breakHours === 'object' && breakHours.minutes !== undefined) {
      const minutes = breakHours.minutes || 0;
      const seconds = breakHours.seconds || 0;
      const totalMinutes = minutes + Math.floor(seconds / 60);
      return `${totalMinutes}m`;
    }

    if (typeof breakHours === 'object' && breakHours.seconds !== undefined) {
      const totalMinutes = Math.floor(breakHours.seconds / 60);
      return `${totalMinutes}m`;
    }

    return DEFAULT_BREAK_HOURS;
  }

  /**
   * Convert a WorkHours object or preformatted string into a "HH:MM" string for calendar display; returns a default if null or invalid.
   * @example
   * formatWorkHoursForCalendar({ hours: 9, minutes: 5 })
   * "09:05"
   * @param {{WorkHours|string|null}} {{workHours}} - Work hours object with hours and minutes, a preformatted "HH:MM" string, or null to use the default calendar hours.
   * @returns {{string}} Return formatted hours as "HH:MM", the original string if provided, or DEFAULT_CALENDAR_HOURS when input is null or invalid.
   **/
  private formatWorkHoursForCalendar(workHours: WorkHours | string | null): string {
    if (!workHours) return DEFAULT_CALENDAR_HOURS;

    if (typeof workHours === 'string') {
      return workHours;
    }

    if (typeof workHours === 'object') {
      const hours = workHours.hours || 0;
      const minutes = workHours.minutes || 0;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    return DEFAULT_CALENDAR_HOURS;
  }

  private formatSessionWorkHours(workHoursString: string): string {
    const parts = workHoursString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      return `${hours}h ${minutes}m`;
    }
    return DEFAULT_HOURS;
  }

  private getSessionField(session: any, newField: string, legacyField: string): string | null {
    return session[newField] || session[legacyField] || null;
  }

  trackBySessionIndex(index: number, record: AttendanceTimeRecord): number {
    return record.sessionIndex ?? index;
  }
}
